import React, { useState } from 'react';
import { Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewsletterSection = () => {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { isDark } = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { email });
      
      if (response.data.success) {
        setIsSubscribed(true);
        setEmail('');
        toast.success(response.data.message);
        
        // Reset after 5 seconds
        setTimeout(() => setIsSubscribed(false), 5000);
      } else {
        toast.error('Failed to subscribe. Please try again.');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="subscribe" className={`relative py-24 overflow-hidden ${isDark ? '' : 'bg-gradient-to-br from-amber-50 via-stone-50 to-orange-50'}`}>
      {/* Background */}
      {isDark && (
        <>
          <div className="absolute inset-0 bg-gradient-to-br from-amber-900/30 via-stone-950 to-stone-950" />
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
          }} />
        </>
      )}

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          {/* Section label */}
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="h-px w-12 bg-amber-500" />
            <span className="text-amber-500 font-medium uppercase tracking-wider text-sm">Stay Connected</span>
            <div className="h-px w-12 bg-amber-500" />
          </div>

          {/* Heading */}
          <h2 className={`text-4xl sm:text-5xl font-bold mb-6 ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
            Join the Sovereignty Movement
          </h2>

          <p className={`text-lg mb-10 max-w-2xl mx-auto ${isDark ? 'text-stone-400' : 'text-stone-600'}`}>
            Subscribe to get weekly insights, episode updates, and exclusive content on living free and staying sovereign.
          </p>

          {/* Form */}
          {isSubscribed ? (
            <div className="inline-flex items-center gap-3 bg-green-500/10 border border-green-500/30 text-green-500 px-6 py-4 rounded-xl">
              <CheckCircle className="w-6 h-6" />
              <span className="font-medium">You're in! Welcome to the movement.</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="newsletter-email-input"
                className={`flex-1 h-14 px-6 text-lg ${
                  isDark 
                    ? 'bg-stone-900/50 border-stone-700 text-stone-100 placeholder:text-stone-500 focus:border-amber-500 focus:ring-amber-500/20' 
                    : 'bg-white border-stone-300 text-stone-800 placeholder:text-stone-400 focus:border-amber-500 focus:ring-amber-500/20'
                }`}
              />
              <Button
                type="submit"
                disabled={isLoading}
                data-testid="newsletter-submit-btn"
                className="bg-amber-500 hover:bg-amber-600 text-stone-950 font-semibold h-14 px-8 text-lg"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 border-2 border-stone-950/30 border-t-stone-950 rounded-full animate-spin" />
                    Subscribing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Subscribe
                    <Send className="w-5 h-5" />
                  </span>
                )}
              </Button>
            </form>
          )}

          <p className={`text-sm mt-6 ${isDark ? 'text-stone-600' : 'text-stone-500'}`}>
            No spam, ever. Unsubscribe anytime.
          </p>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSection;
